// pages/centralStock/transferStockDialog.jsx
import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, CircularProgress, FormControl,
    InputLabel, Select, MenuItem, Typography, Box,
    Alert, Stack, Divider
} from '@mui/material';
import {
    SwapHoriz as SwapHorizIcon,
    Warehouse as WarehouseIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { stockTransferAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const TransferStockDialog = ({ open, onClose, selectedItem, branches, onSuccess }) => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();
    const [transferMode, setTransferMode] = useState('central_to_branch');

    const userBranchId = user?.branch;

    const transferFormik = useFormik({
        initialValues: {
            sourceBranchId: userBranchId || '',
            branchId: '',
            quantity: 1,
            notes: '',
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            sourceBranchId: transferMode === 'branch_to_branch'
                ? Yup.string().required(t('centralStock.sourceBranchRequired', 'Source branch is required'))
                : Yup.string(),
            branchId: Yup.string()
                .required(t('centralStock.branchRequired', 'Branch is required'))
                .test(
                    'not-same-as-source',
                    t('stock.cannotTransferToSameBranch', 'Cannot transfer to the same branch'),
                    function (value) {
                        if (transferMode === 'branch_to_branch') {
                            return value !== this.parent.sourceBranchId;
                        }
                        return true;
                    }
                ),
            quantity: Yup.number()
                .min(1, t('centralStock.minQuantity', 'Quantity must be at least 1'))
                .max(
                    selectedItem?.quantity || 0,
                    `Cannot exceed available quantity (${selectedItem?.quantity || 0})`
                )
                .required(t('centralStock.quantityRequired', 'Quantity is required')),
            notes: Yup.string().max(500).optional(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                if (transferMode === 'central_to_branch') {
                    await stockTransferAPI.transferCentralToBranch({
                        centralStockId: selectedItem._id,
                        branchId: values.branchId,
                        quantity: Number(values.quantity),
                        notes: values.notes || undefined,
                    });
                } else {
                    // Branch-to-branch: need the stock item ID (not central stock ID)
                    // The selectedItem here is a CentralStock doc, so we need to find the linked Stock item
                    // For now we pass the centralStockId and let the backend resolve it
                    await stockTransferAPI.transferBranchToBranch({
                        stockItemId: selectedItem.linkedStockItem || selectedItem._id,
                        sourceBranchId: values.sourceBranchId,
                        destinationBranchId: values.branchId,
                        quantity: Number(values.quantity),
                        notes: values.notes || undefined,
                    });
                }

                toast.success(t('centralStock.transferSuccess', 'Stock transferred successfully'));
                onSuccess?.();
                handleClose();
            } catch (error) {
                toast.error(
                    error.response?.data?.message ||
                    t('centralStock.transferError', 'Failed to transfer stock')
                );
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleClose = () => {
        transferFormik.resetForm();
        setTransferMode('central_to_branch');
        onClose();
    };

    const availableQty = selectedItem?.quantity || 0;

    const destinationBranches = transferMode === 'branch_to_branch'
        ? branches.filter(b => b._id !== transferFormik.values.sourceBranchId)
        : branches;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <SwapHorizIcon color="primary" />
                    {t('centralStock.transferTitle', 'Transfer Stock')}
                </Box>
            </DialogTitle>
            <form onSubmit={transferFormik.handleSubmit}>
                <DialogContent>
                    {/* Item info */}
                    <TextField
                        fullWidth
                        margin="dense"
                        label={t('centralStock.itemName', 'Item Name')}
                        value={selectedItem?.name || ''}
                        disabled
                        sx={{ mb: 1 }}
                    />

                    {/* Transfer Mode Toggle */}
                    <Stack direction="row" spacing={1} mb={2} mt={1}>
                        <Button
                            variant={transferMode === 'central_to_branch' ? 'contained' : 'outlined'}
                            startIcon={<WarehouseIcon />}
                            onClick={() => {
                                setTransferMode('central_to_branch');
                                transferFormik.setFieldValue('sourceBranchId', '');
                            }}
                            size="small"
                            fullWidth
                        >
                            {t('stock.centralToBranch', 'Central → Branch')}
                        </Button>
                        <Button
                            variant={transferMode === 'branch_to_branch' ? 'contained' : 'outlined'}
                            startIcon={<SwapHorizIcon />}
                            onClick={() => {
                                setTransferMode('branch_to_branch');
                                transferFormik.setFieldValue('sourceBranchId', userBranchId || '');
                            }}
                            size="small"
                            fullWidth
                        >
                            {t('stock.branchToBranch', 'Branch → Branch')}
                        </Button>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Available Quantity Info */}
                    <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
                        <Typography variant="body2">
                            {transferMode === 'central_to_branch'
                                ? `${t('centralStock.availableQuantity', 'Central Stock Available')}: ${availableQty}`
                                : transferFormik.values.sourceBranchId
                                    ? `${t('stock.branchStockAvailable', 'Branch Stock Available')}: ${selectedItem?.branchQuantities?.[transferFormik.values.sourceBranchId] || 0
                                    }`
                                    : t('stock.selectSourceBranch', 'Select a source branch')
                            }
                        </Typography>
                        {selectedItem?.averageCost > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                {t('stock.currentWac', 'Current WAC')}: {(selectedItem.averageCost || 0).toFixed(2)}
                            </Typography>
                        )}
                    </Alert>

                    {/* Source Branch (B2B only) */}
                    {transferMode === 'branch_to_branch' && (
                        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                            <InputLabel>{t('stock.sourceBranch', 'Source Branch')}</InputLabel>
                            <Select
                                name="sourceBranchId"
                                value={transferFormik.values.sourceBranchId}
                                onChange={transferFormik.handleChange}
                                label={t('stock.sourceBranch', 'Source Branch')}
                                error={
                                    transferFormik.touched.sourceBranchId &&
                                    !!transferFormik.errors.sourceBranchId
                                }
                                disabled={!isSuperAdmin && !!userBranchId}
                            >
                                {branches.map((branch) => (
                                    <MenuItem key={branch._id} value={branch._id}>
                                        {branch.name} {branch.location ? `- ${branch.location}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                            {transferFormik.touched.sourceBranchId && transferFormik.errors.sourceBranchId && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                    {transferFormik.errors.sourceBranchId}
                                </Typography>
                            )}
                        </FormControl>
                    )}

                    {/* Destination Branch */}
                    <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                        <InputLabel>{t('centralStock.branch', 'Destination Branch')}</InputLabel>
                        <Select
                            name="branchId"
                            value={transferFormik.values.branchId}
                            onChange={transferFormik.handleChange}
                            label={t('centralStock.branch', 'Destination Branch')}
                            error={
                                transferFormik.touched.branchId &&
                                !!transferFormik.errors.branchId
                            }
                        >
                            {destinationBranches.map((branch) => (
                                <MenuItem key={branch._id} value={branch._id}>
                                    {branch.name} {branch.location ? `- ${branch.location}` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                        {transferFormik.touched.branchId && transferFormik.errors.branchId && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {transferFormik.errors.branchId}
                            </Typography>
                        )}
                    </FormControl>

                    {/* Quantity */}
                    <TextField
                        fullWidth
                        margin="dense"
                        name="quantity"
                        label={t('centralStock.quantityToTransfer', 'Quantity to Transfer')}
                        type="number"
                        value={transferFormik.values.quantity}
                        onChange={transferFormik.handleChange}
                        error={
                            transferFormik.touched.quantity &&
                            !!transferFormik.errors.quantity
                        }
                        helperText={
                            (transferFormik.touched.quantity && transferFormik.errors.quantity) ||
                            (transferMode === 'central_to_branch'
                                ? `${t('stock.maxAvailable', 'Max')}: ${availableQty}`
                                : transferFormik.values.sourceBranchId
                                    ? `${t('stock.maxAvailable', 'Max')}: ${selectedItem?.branchQuantities?.[transferFormik.values.sourceBranchId] || 0
                                    }`
                                    : '')
                        }
                        inputProps={{
                            min: 1,
                            max: transferMode === 'central_to_branch'
                                ? availableQty
                                : (selectedItem?.branchQuantities?.[transferFormik.values.sourceBranchId] || 0)
                        }}
                        sx={{ mb: 2 }}
                    />

                    {/* Notes */}
                    <TextField
                        fullWidth
                        margin="dense"
                        name="notes"
                        label={t('stock.transferNotes', 'Notes (optional)')}
                        multiline
                        rows={2}
                        value={transferFormik.values.notes}
                        onChange={transferFormik.handleChange}
                        error={transferFormik.touched.notes && !!transferFormik.errors.notes}
                        helperText={transferFormik.touched.notes && transferFormik.errors.notes}
                        inputProps={{ maxLength: 500 }}
                    />

                    {/* Quantity exceeds warning */}
                    {transferMode === 'central_to_branch' &&
                        transferFormik.values.quantity > availableQty &&
                        availableQty > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {t('stock.quantityExceedsAvailable', 'Quantity exceeds available stock!')}
                            </Alert>
                        )}

                    {transferMode === 'branch_to_branch' &&
                        transferFormik.values.sourceBranchId &&
                        transferFormik.values.quantity > (selectedItem?.branchQuantities?.[transferFormik.values.sourceBranchId] || 0) && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {t('stock.quantityExceedsAvailable', 'Quantity exceeds available stock!')}
                            </Alert>
                        )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            transferFormik.isSubmitting ||
                            (transferMode === 'central_to_branch' && transferFormik.values.quantity > availableQty) ||
                            (transferMode === 'branch_to_branch' &&
                                transferFormik.values.quantity > (selectedItem?.branchQuantities?.[transferFormik.values.sourceBranchId] || 0)) ||
                            availableQty === 0
                        }
                        startIcon={
                            transferFormik.isSubmitting
                                ? <CircularProgress size={18} />
                                : <SwapHorizIcon />
                        }
                    >
                        {t('centralStock.transfer', 'Transfer')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default TransferStockDialog;
